/**
 * Cosmetic debris (PRD §5.4, §6.2 — perf cap).
 *
 * Spawns short-lived rigid chunks on hit / kill events. Honors the
 * hard cap on active dynamic bodies (PRD §6.2) — when the cap is hit,
 * the oldest chunks despawn so late-match destruction events stay
 * snappy.
 *
 * Chunks are unit boxes scaled by the impulse profile. The seeded RNG
 * picks chunk direction within the profile cone so two viewings of
 * the same replay produce the same visual beats.
 */

import { useEffect, useRef, useState } from "react";
import { RigidBody, type RapierRigidBody } from "@react-three/rapier";
import { SeededRng } from "../determinism/cosmeticSeed";
import type { ImpulseProfile } from "./impulses";

interface ChunkSpec {
  id: number;
  origin: [number, number, number];
  color: string;
  impulse: [number, number, number];
  angular: [number, number, number];
  scale: number;
  spawnedAt: number;
  /** When `frozen=true` the chunk converts to a kinematic decal-like sleeper. */
  frozen: boolean;
}

export interface DebrisSpawnRequest {
  /** Stable per-request id (event uuid or `tick*1000+seq`). */
  id: number;
  origin: [number, number, number];
  /** Cosmetic seed mixed into chunk direction selection. */
  seed: number;
  /** Hit-normal direction in world space (defaults to +Y). */
  normal?: [number, number, number];
  profile: ImpulseProfile;
  /** Base color for chunks — picks up the destroyed part's palette. */
  color: string;
}

export interface DebrisFieldProps {
  /** Requests to spawn this frame. The component dedupes by `id`. */
  requests: ReadonlyArray<DebrisSpawnRequest>;
  /** Hard cap on active dynamic chunks (PRD §6.2). */
  maxActive?: number;
  /** ms after spawn before chunks freeze into static decals. */
  freezeAfterMs?: number;
  /** ms after spawn before chunks despawn entirely. Must be > freeze. */
  despawnAfterMs?: number;
}

const DEFAULTS = { maxActive: 240, freezeAfterMs: 2500, despawnAfterMs: 8000 };

export function DebrisField({
  requests,
  maxActive = DEFAULTS.maxActive,
  freezeAfterMs = DEFAULTS.freezeAfterMs,
  despawnAfterMs = DEFAULTS.despawnAfterMs,
}: DebrisFieldProps) {
  const [chunks, setChunks] = useState<ChunkSpec[]>([]);
  const seenIds = useRef<Set<number>>(new Set());

  // Spawn new chunks for unseen requests. Enforces the cap by dropping
  // oldest entries; never blocks a request — the cap is rolling.
  useEffect(() => {
    if (requests.length === 0) return;
    const now = performance.now();
    const fresh: ChunkSpec[] = [];

    for (const req of requests) {
      if (seenIds.current.has(req.id)) continue;
      seenIds.current.add(req.id);

      const rng = new SeededRng(req.seed ^ req.id);
      const normal = req.normal ?? [0, 1, 0];

      for (let i = 0; i < req.profile.chunkCount; i++) {
        const dir = jitterCone(rng, normal, req.profile.cone, req.profile.upBias);
        const mag = req.profile.linear * (0.6 + rng.next() * 0.8);
        const ang: [number, number, number] = [
          (rng.next() - 0.5) * req.profile.angular * 2,
          (rng.next() - 0.5) * req.profile.angular * 2,
          (rng.next() - 0.5) * req.profile.angular * 2,
        ];
        fresh.push({
          id: req.id * 1000 + i,
          origin: req.origin,
          color: req.color,
          impulse: [dir[0] * mag, dir[1] * mag, dir[2] * mag],
          angular: ang,
          scale: 0.05 + rng.next() * 0.07,
          spawnedAt: now,
          frozen: false,
        });
      }
    }

    if (fresh.length === 0) return;

    setChunks((prev) => {
      const next = [...prev, ...fresh];
      // Cap enforcement — drop oldest first.
      if (next.length > maxActive) next.splice(0, next.length - maxActive);
      return next;
    });
  }, [requests, maxActive]);

  // Lifecycle tick — freeze + despawn old chunks.
  useEffect(() => {
    const tick = () => {
      const now = performance.now();
      setChunks((prev) =>
        prev
          .map((c) =>
            !c.frozen && now - c.spawnedAt > freezeAfterMs ? { ...c, frozen: true } : c,
          )
          .filter((c) => now - c.spawnedAt < despawnAfterMs),
      );
    };
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [freezeAfterMs, despawnAfterMs]);

  return (
    <group name="debris-field">
      {chunks.map((c) => (
        <DebrisChunk key={c.id} chunk={c} />
      ))}
    </group>
  );
}

function DebrisChunk({ chunk }: { chunk: ChunkSpec }) {
  const bodyRef = useRef<RapierRigidBody>(null);

  useEffect(() => {
    const b = bodyRef.current;
    if (!b || chunk.frozen) return;
    b.applyImpulse({ x: chunk.impulse[0], y: chunk.impulse[1], z: chunk.impulse[2] }, true);
    b.applyTorqueImpulse({ x: chunk.angular[0], y: chunk.angular[1], z: chunk.angular[2] }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <RigidBody
      ref={bodyRef}
      type={chunk.frozen ? "fixed" : "dynamic"}
      colliders="cuboid"
      position={chunk.origin}
      linearDamping={0.3}
      angularDamping={0.4}
      restitution={0.1}
      friction={0.6}
    >
      <mesh castShadow receiveShadow>
        <boxGeometry args={[chunk.scale, chunk.scale, chunk.scale]} />
        <meshStandardMaterial color={chunk.color} roughness={0.6} metalness={0.3} />
      </mesh>
    </RigidBody>
  );
}

/**
 * Pick a random direction inside a cone around `normal`. Adds an
 * up-bias so explosive / reactor profiles lift debris.
 */
function jitterCone(
  rng: SeededRng,
  normal: [number, number, number],
  half: number,
  upBias: number,
): [number, number, number] {
  // Random direction in unit-sphere (rejection method is fine for the
  // small per-frame count we'll hit).
  let x = 0,
    y = 0,
    z = 0,
    lenSq = 0;
  do {
    x = rng.next() * 2 - 1;
    y = rng.next() * 2 - 1;
    z = rng.next() * 2 - 1;
    lenSq = x * x + y * y + z * z;
  } while (lenSq < 0.001 || lenSq > 1);

  // Normalize.
  const len = Math.sqrt(lenSq);
  x /= len;
  y /= len;
  z /= len;

  // Blend toward `normal` so direction stays inside the cone half-angle.
  const blend = 1 - half / Math.PI; // half=PI → blend=0, half=0 → blend=1
  x = x * (1 - blend) + normal[0] * blend;
  y = y * (1 - blend) + normal[1] * blend + upBias;
  z = z * (1 - blend) + normal[2] * blend;

  // Re-normalize.
  const m = Math.hypot(x, y, z) || 1;
  return [x / m, y / m, z / m];
}
